import { IClinicAutoComplete, IHealthRecord, ITownship, Typestate } from "@interfaces";
import { memo, useEffect, useState } from "react";
import * as Yup from "yup";
import { Input, Form, Row, Col, DatePicker, Select, Radio, AutoComplete } from "antd";

import { useTranslation } from "react-i18next";

import { FEMALE, MALE, STEP_TWO, enumSubdomain } from "@configs";
import { useDispatch, useSelector } from "react-redux";
import { selectApp, setLoading } from "@redux";
import { useNotify } from "@utils";
import { useHistory } from "react-router";
import { SharedButton } from "@components";
import styled from "styled-components";
import { useYup } from "@validations";
import { contraceptionApi, regionAndTownshipApi } from "@api";
import dayjs from "dayjs";

interface IProps {
  data: IHealthRecord;
  callback: (data: IHealthRecord, step: number) => void;
}
export const StepOne = memo(({ data, callback }: IProps) => {
  const { t } = useTranslation();
  const { YupOne } = useYup();
  const dispatch = useDispatch();
  const { domain } = useSelector(selectApp);
  const history = useHistory();
  const { success, error } = useNotify();
  const [regions, setRegions] = useState<any>([]);
  const [townships, setTownships] = useState<ITownship[]>([]);
  const [stepOneData, setStepOneData] = useState<IHealthRecord>({});
  const [healtForm] = Form.useForm();
  const formOneSchema = Yup.object().shape(YupOne);
  const [fetching, setFetching] = useState<boolean>(false);
  const [autoCompleteclinics, setAutocompleteClinics] = useState<IClinicAutoComplete[]>([]);
  const [clinicId, setClinicId] = useState<string>("");

  const regionId: Typestate = Form.useWatch("client_region_id", healtForm);
  const townshipId: Typestate = Form.useWatch("client_township_id", healtForm);

  const isAdminApp = () => domain === enumSubdomain.STG_ADMIN || domain === enumSubdomain.ADMIN;

  useEffect(() => {
    searchTownshipsByRegion(regionId || "");
    healtForm.setFieldValue("client_township_id", "");
  }, [regionId]);

  useEffect(() => {
    setStepOneData(data);
    
    healtForm.setFieldsValue({
      ...data,
      client_visit_date: dayjs(data?.client_visit_date),
      clinic_name: data?.clinic_name,
    });
  }, [data]);

  useEffect(() => {
    if (townships.length > 0 && data) {      
      const township = townships.find((item) => item?.stateId === data?.client_township_id);      
      healtForm.setFieldValue("client_township_id", township?.stateId);
    }
  }, [townships, data]);

  useEffect(() => {
    searchRegions();
  }, []);

  const onFinish = async (values: IHealthRecord) => {
    try {
      console.log(values);
      await callback({ ...values }, STEP_TWO);
    } catch (err: any) {
      error(err.message);
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await formOneSchema.validateSyncAt(field, { [field]: value });
    },
  };

  const searchRegions = async () => {
    try {
      setFetching(true);
      const res = await regionAndTownshipApi.getRegions();
      setRegions(res.data?.data);
      setFetching(false);
    } catch (err: any) {
      setFetching(false);
    } finally {
      setFetching(false);
    }
  };

  const searchTownshipsByRegion = async (region_id: string) => {
    try {
      setFetching(true);
      const res = await regionAndTownshipApi.getTownships(region_id);
      setTownships(res.data?.data);
      setFetching(false);
    } catch (err: any) {
      setFetching(false);
    } finally {
      setFetching(false);
    }
  };

  const searchClinic = async (value: string) => {
    try {
      setFetching(true);
      const res = await contraceptionApi.autocompleteSearchClinic(value);
      setAutocompleteClinics(getClinicValues(res.data?.data));
      setFetching(false);
    } catch (err: any) {
      setFetching(false);
    } finally {
      setFetching(false);
    }
  };
  const getClinicValues = (data: IClinicAutoComplete[]) => {
    return data.reduce<IClinicAutoComplete[]>((a, c) => {
      a.push({ ...c, value: c?.clinicName || "" });
      return a;
    }, []);
  };
  const onSelectClinic = (value: string, option: IClinicAutoComplete) => {
    console.log(option?._id);
    healtForm.setFieldValue("clinic_id", option?._id);
    // setClinicId(option?._id || "");
  };
  return (
    <Form
      form={healtForm}
      name="basic"
      initialValues={stepOneData}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
    >
      {isAdminApp() && (
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.clinic_name")} name="clinic_name" rules={[yupSync]}>
              <AutoComplete
                options={autoCompleteclinics}
                onSelect={onSelectClinic}
                onSearch={searchClinic}
                placeholder="Type Clinic Name"
              />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item name={"clinic_id"}>
              <Input type="hidden" />
            </Form.Item>
          </Col>
        </Row>
      )}
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.client_visit_date")} name="client_visit_date" rules={[yupSync]}>
            <DatePicker />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.client_name")} name="client_name" rules={[yupSync]}>
            <Input type="text" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item
            label={t("page.client_phoneNumber")}
            name="client_phoneNumber"
            rules={[yupSync]}
          >
            <Input type="number" />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.client_age")} name="client_age" rules={[yupSync]}>
            <Input type="number" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item name={"client_region_id"} label={t("page.region_state")} rules={[yupSync]}>
            <Select>
              {regions.map((item: any) => (
                <Select.Option value={item.regionId} key={item.regionId}>
                  {item.regionName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item name={"client_township_id"} label={t("page.township")} rules={[yupSync]}>
            <Select value={townshipId}>
              {regionId &&
                townships.map((item: any) => (
                  <Select.Option value={item.stateId} key={item.stateId}>
                    {item.stateName}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item name={"client_gender"} label={t("page.Gender")} rules={[yupSync]}>
            <Radio.Group>
              <Radio value={FEMALE}>{t("page.female")}</Radio>
              <Radio value={MALE}>{t("page.male")}</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <StyledFormAction>
            <SharedButton type="primary" htmlType="submit" size="middle">
              {t("page.next")}
            </SharedButton>
          </StyledFormAction>
        </Col>
      </Row>
    </Form>
  );
});

const StyledFormAction = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 40px;
`;
